import React, { useEffect, useRef, useState, useCallback } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import styles from './EditorExtension_ImageList.module.css';
import { useCCContext } from '../CC/CCContext';
import { CC } from '../CC/CC';
import * as ccUtils from '../CC/utils';
import { generateQuery, replaceBasketIdSubqueries, postWithCache } from '../CC/utils';
import { getImages } from '../Result/getImages';
import { Gallery } from 'react-grid-gallery';

const ImageWithFallback = ({ src, alt, width, height, style }) => {
  const [imgSrc, setImgSrc] = useState(src);

  const onError = () => {
    setImgSrc(`https://collections.viewallonne.be/cc/imageproxy.ashx?server=localhost&port=17592&filename=images\nondisponible.cci&height=${height}&width=${width}&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=30&passepartoutheight=0&passepartoutcolor=ffffff&cache=yes`);
  };

  return <img src={imgSrc} alt={alt} onError={onError} width={width} height={height} style={style} />;
};

const ReactGridGalleryImageList = ({ records, state, props, imageHeight, numImages }) => {
  const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true); // Added loading state

  useEffect(() => {
    const loadImages = async () => {
      const imagePromises = records.map(async (record, index) => {
        const allImages = getImages(record);
        console.log(allImages);
        const imageUrl = allImages["imagebare"]?.[0]?.url + '&bg=ffffff&height=' + imageHeight || `https://collections.viewallonne.be/cc/imageproxy.ashx?server=localhost&port=17592&filename=images\nondisponible.cci&height=${imageHeight}&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=30&passepartoutheight=0&passepartoutcolor=ffffff&cache=yes`;

        try {
          const img = new Image();
          img.src = imageUrl;
          await img.decode();
          return {
            src: imageUrl,
            width: img.width,
            height: img.height,
            customOverlay: (
              <div className="custom-overlay__caption">
                <div className={styles.recordOverlay}>
                  <div className={styles.recordOverlayInner}>
                    {record?.data?.record?.title?.title}
                    {record?.data?.record?.objectnumber}
                    {record?.data?.record?.sitename}
                  </div>
                </div>
              </div>
            ),
            index: index,
            alt: `Image ${index + 1}`,
          };
        } catch (error) {
          return {
            src: `https://collections.viewallonne.be/cc/imageproxy.ashx?server=localhost&port=17592&filename=images\nondisponible.cci&height=${imageHeight}&width=${imageHeight}&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=30&passepartoutheight=0&passepartoutcolor=ffffff&cache=yes`,
            width: imageHeight,
            height: imageHeight,
            customOverlay: (
              <div className="custom-overlay__caption">
                <div className={styles.recordOverlay}>
                  <div className={styles.recordOverlayInner}>
                    {record?.data?.record?.title?.title}
                  </div>
                </div>
              </div>
            ),
            index: index,
            alt: `Image ${index + 1}`,
          };
        }
      });

      const loadedImages = await Promise.all(imagePromises);
      setImages(loadedImages);
      setLoading(false); // Set loading to false after images are loaded
    };

    loadImages();
  }, [records, imageHeight, numImages]);

  const handleClick = (index) => {
    if (!props.editor.isEditable) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('albumexternal')) {
        window.open(`${window.location.origin}${window.location.pathname}?state=${state}&index=${index}`, '_blank');
      } else {

        if (state) {
          try {
            const defaultCCObj = new CC();
            const startUPCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(state));
            setCCContext({
              cc: startUPCCObj,
              first: Math.floor((index - 1) / recordsPerPage) * recordsPerPage + 1,
              selectedRecord: index,
              viewMode: 'list',
            });
          } catch (error) {
            console.error("Error parsing state:", error);
          }
        } else {
          clearSearch();
          setViewMode('list');
        }
      }
    }
  };

  // Map images to include the onClick handler
  const galleryImages = images.map((image) => ({
    ...image,
    customOverlay: image.customOverlay,
    // Optionally, you can include custom properties if needed
  }));

  return (
    <div className={styles.galleryContainer}>
      {loading ? (
        <div className={styles.placeholderContainer}>
          {Array.from({ length: numImages }).map((_, idx) => (
            <div
              key={idx}
              className={styles.placeholder}
            >
              <div className={styles.loadingSpinner}></div>
            </div>
          ))}
        </div>
      ) : (
        <Gallery
          images={galleryImages}
          enableImageSelection={false}
          onClick={(index) => handleClick(galleryImages[index].index + 1)}
          margin={5}
          rowHeight={imageHeight}
          customStyles={{
            // Add any custom styles if necessary
          }}
        />
      )}
    </div>
  );
};

export default Node.create({
  name: 'reactComponentImageList',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      selected: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-selected') === 'true',
        renderHTML: (attributes) => ({
          'data-selected': attributes.selected
        }),
      },
      state: {
        default: '',
        parseHTML: (element) => {
          const state = element.getAttribute('data-state');
          return state;
        },
        renderHTML: (attributes) => ({
          'data-state': attributes.state
        }),
      },
      imageHeight: {
        default: 100,
        parseHTML: (element) => parseInt(element.getAttribute('data-image-height') || '100', 10),
        renderHTML: (attributes) => ({
          'data-image-height': attributes.imageHeight
        }),
      },
      numImages: {
        default: 100,
        parseHTML: (element) => parseInt(element.getAttribute('data-num-images') || '100', 10),
        renderHTML: (attributes) => ({
          'data-num-images': attributes.numImages
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'ImageList',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['ImageList', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props) => {
  const { selected, updateAttributes } = props;
  const { state, imageHeight, numImages } = props.node.attrs;
  const [records, setRecords] = useState([]);
  const componentRef = useRef({});
  const { user } = useCCContext();

  const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();

  useEffect(() => {
    const fetchStateImageList = async () => {
      try {
        let stateCC = {};
        if (state && state !== '') {
          stateCC = JSON.parse(state);
        } else {
          stateCC = new CC();
        }
        let query = generateQuery(stateCC, user);
        query = await replaceBasketIdSubqueries(query);
        const payload = {
          action: "get",
          command: "search",
          query: query,
          range: `1-${props.node.attrs.numImages}`,
          responseformat: "json"
        };
        const response = await postWithCache(process.env.REACT_APP_BASE_URL, payload, componentRef.current);
        setRecords(response.records.record);
      } catch (error) {
        setRecords([]);
        console.error("Error fetching state imageList:", error);
      }
    };

    fetchStateImageList();
  }, [state, imageHeight, numImages, user]);

  useEffect(() => {
    const handleSelection = () => {
      updateAttributes({ selected: props.editor.isActive('reactComponentImageList') });
    };

    props.editor.on('selectionUpdate', handleSelection);

    return () => {
      props.editor.off('selectionUpdate', handleSelection);
    };
  }, [props.editor, updateAttributes]);

  const handleDoubleClick = (event) => {
    if (props.editor.isEditable) {
      event.stopPropagation();
      const { state, query, htmlStyle, imageHeight, numImages } = props.node.attrs;
      const customEvent = new CustomEvent('showInsertContentPopup', {
        detail: {
          extension: 'ImageList',
          props: { state, query, htmlStyle, imageHeight, numImages },
          pos: props.getPos(),
          node: props.node
        },
        bubbles: true,
        cancelable: true
      });
      event.target.dispatchEvent(customEvent);
    }
  };

  return (
    <NodeViewWrapper as="span" className={`react-component ${selected ? 'selected' : ''}`}>
      <div className={styles.hyperlink} onDoubleClick={handleDoubleClick}>
        <ReactGridGalleryImageList records={records} state={state} props={props} imageHeight={imageHeight} numImages={numImages} />
      </div>
    </NodeViewWrapper>
  );
};
